import { useState } from 'react';
import { Navigate } from 'react-router';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useUrlParam } from '#hooks/useUrlParam.ts';
import { Login } from './Login.tsx';
import { MagicLinkSent } from './magic-link-sent/MagicLinkSent.tsx';

export function LoginPage() {
  const auth = useAuth();
  const location = useAppLocation();
  const [magicLinkSent, setMagicLinkSent] = useState<boolean>(false);
  const [emailInParams, setEmailInParams] = useUrlParam('email');
  const [email, setEmail] = useState<string>(emailInParams ?? '');

  // Clear email from url params after grabbing it.
  if (emailInParams && email) {
    setEmailInParams();
  }

  // Guard the whole page, not just the login form: auth state can change while the
  // magic-link-sent screen is showing (the link opened in another tab, or a passkey
  // prompt completed after the form was submitted), and that screen has no other
  // way to react. Redirect to the app if authenticated; it will redirect back to
  // login if the tokens are invalid.
  const authState = auth.getAuthState();
  if (authState === 'authenticated') {
    return <Navigate to="/" replace={true} />;
  }
  // If we have a user token already, show the mfa page. Forward state.from: this branch only
  // fires when a userToken is present (same user by construction, no account-switch risk), and
  // preserving it keeps the post-MFA redirect target if a transient unauthenticated snapshot
  // bounced through here on the way to /auth/mfa.
  if (authState === 'mfaRequired') {
    return <Navigate to="/auth/mfa" state={location.state} replace={true} />;
  }

  if (magicLinkSent) {
    return (
      <MagicLinkSent
        email={email}
        onTryAgain={() => {
          setMagicLinkSent(false);
        }}
      />
    );
  }

  return (
    <Login
      email={email}
      onSuccessfulSubmit={(emailAddress) => {
        setMagicLinkSent(true);
        setEmail(emailAddress);
      }}
    />
  );
}
